import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Enhanced Parallax System
 * 
 * Provides smooth parallax effects for images and elements using GSAP ScrollTrigger
 * for better performance and more control options.
 * 
 * Usage:
 * - Add data-parallax="speed" to any element (e.g., data-parallax="0.5")
 * - Use data-parallax-direction="up" or "down" to control direction
 * - Use data-parallax-axis="x" for horizontal parallax
 * - Wrap elements in .parallax-container for better control
 */

// Check if device is desktop with mouse for performance optimization
function isDesktopWithMouse() {
  const hasHover = window.matchMedia('(hover: hover)').matches;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const isDesktopSize = window.innerWidth >= 1024;
  
  return hasHover && !isMobile && isDesktopSize;
}

// Default values
const DEFAULTS = {
  speed: 0.5,
  direction: 'down',
  axis: 'y',
  start: 'top bottom',
  end: 'bottom top'
};

/**
 * Initialize the parallax system
 */
export function initParallax() {
  // Skip parallax on mobile devices for better performance
  if (!isDesktopWithMouse()) {
    return;
  }

  try {
    // Initialize elements with data-parallax attribute
    const parallaxElements = document.querySelectorAll('[data-parallax]');
    
    if (parallaxElements.length === 0) {
      return;
    }

    parallaxElements.forEach(element => {
      createParallaxAnimation(element);
    });

    
  } catch (error) {
    console.error('Error initializing parallax system:', error);
  }
}

/**
 * Create parallax animation for a single element
 */
function createParallaxAnimation(element) {
  // Validate element
  if (!element || !element.nodeType) {
    console.warn('Invalid element provided to createParallaxAnimation');
    return;
  }

  // Get configuration from data attributes
  const speed = parseFloat(element.dataset.parallax) || DEFAULTS.speed;
  const direction = element.dataset.parallaxDirection || DEFAULTS.direction;
  const axis = element.dataset.parallaxAxis || DEFAULTS.axis;
  const start = element.dataset.parallaxStart || DEFAULTS.start;
  const end = element.dataset.parallaxEnd || DEFAULTS.end;

  // Validate speed
  if (isNaN(speed) || speed < 0 || speed > 2) {
    console.warn(`Invalid parallax speed: ${speed}. Using default.`, element);
    speed = DEFAULTS.speed;
  }

  // Determine direction multiplier
  const directionMultiplier = direction === 'up' ? -1 : 1;
  
  // Determine axis
  const isHorizontal = axis === 'x';
  const transformProperty = isHorizontal ? 'x' : 'y';
  const transformValue = isHorizontal ? 
    `${speed * 100 * directionMultiplier}vw` : 
    `${speed * 100 * directionMultiplier}vh`;

  // Set initial state
  gsap.set(element, {
    [transformProperty]: 0,
    willChange: 'transform'
  });

  // Create ScrollTrigger animation
  gsap.to(element, {
    [transformProperty]: transformValue,
    ease: 'none',
    scrollTrigger: {
      trigger: element,
      start: start,
      end: end,
      scrub: true,
      onComplete: () => {
        // Remove will-change after animation completes
        gsap.set(element, { willChange: 'auto' });
      }
    }
  });
}

/**
 * Legacy parallax function for backward compatibility
 * Uses the old scroll-based approach for elements with .parallax-image class
 */
export function initLegacyParallax() {
  const parallaxContainers = document.querySelectorAll('.parallax-container');
  const parallaxImages = document.querySelectorAll('.parallax-image');
  
  if (!parallaxContainers.length || !parallaxImages.length) return;

  let ticking = false;
  const defaultParallaxFactor = 0.25;

  function updateParallax() {
    const scrollY = window.scrollY;
    
    parallaxImages.forEach(image => {
      const container = image.closest('.parallax-container');
      if (!container) return;

      const speed = parseFloat(image.dataset.parallaxSpeed) || defaultParallaxFactor;
      const direction = image.dataset.parallaxDirection === 'up' ? -1 : 1;

      const containerRect = container.getBoundingClientRect();
      const containerTop = containerRect.top + scrollY;
      const containerHeight = containerRect.height;
      
      if (containerTop + containerHeight > scrollY && containerTop < scrollY + window.innerHeight) {
        const scrolled = scrollY - containerTop;
        const translateY = scrolled * speed * direction;
        
        image.style.transform = `translate3d(0, ${translateY}px, 0)`;
      }
    });
    
    ticking = false;
  }

  function onScroll() {
    if (!ticking) {
      window.requestAnimationFrame(updateParallax);
      ticking = true;
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  updateParallax();
}

/**
 * Refresh ScrollTrigger instances
 */
export function refreshParallax() {
  ScrollTrigger.refresh();
}

/**
 * Kill all parallax ScrollTrigger instances
 */
export function killParallax() {
  ScrollTrigger.killAll();
}

/**
 * Get parallax statistics for debugging
 */
export function getParallaxStats() {
  const parallaxElements = document.querySelectorAll('[data-parallax]');
  const legacyImages = document.querySelectorAll('.parallax-image');
  const scrollTriggers = ScrollTrigger.getAll();
  
  return {
    totalParallaxElements: parallaxElements.length,
    totalLegacyImages: legacyImages.length,
    totalScrollTriggers: scrollTriggers.length,
    isParallaxEnabled: isDesktopWithMouse()
  };
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initParallax);
} else {
  initParallax();
} 